import { useEffect, useState } from "react";
import { Sparkles, Check, X, GitBranchPlus, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, BRANCH_COLORS, BRANCH_TITLES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { t } from "@/lib/i18n";

export const TreeProposals = ({ onApplied }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const load = () => api.get("/tree/proposals").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try { const r = await api.post("/tree/proposals/generate"); toast.success(r.data.created ? t("{n} nuove proposte dal Direttore di Metodo", { n: r.data.created }) : t("Nessun concetto nuovo da valutare: carica altri documenti")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Generazione non riuscita")); } finally { setBusy(false); }
  };
  const decide = async (id, action) => {
    try { const r = await api.post(`/tree/proposals/${id}/${action}`); toast.success(t(action === "approve" ? "Albero aggiornato" : "Proposta scartata")); load(); if (action === "approve") onApplied?.(r.data.node_id); }
    catch { toast.error(t("Operazione non riuscita")); }
  };

  return (
    <section data-testid="tree-proposals" className="surface p-4 border-primary/40">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button data-testid="tree-proposals-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-2 font-heading font-semibold text-left">
          <Sparkles size={15} className="text-primary" /> {t("Proposte di crescita dell'albero")}
          <span data-testid="tree-proposals-count" className="num text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary">{items.length}</span>
        </button>
        <Button data-testid="tree-proposals-generate" size="sm" variant="outline" onClick={generate} disabled={busy}>{busy ? t("Il Direttore sta valutando…") : t("Cerca nuovi concetti")}</Button>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          {items.length === 0 && <p className="text-xs text-muted-foreground">{t("Nessuna proposta in attesa. I concetti nuovi trovati nei documenti caricati compaiono qui, da approvare con un click.")}</p>}
          {items.map((p) => (
            <div key={p.id} data-testid={`proposal-${p.id}`} className="surface-elevated p-3 flex gap-3" style={{ borderLeft: `3px solid ${BRANCH_COLORS[p.branch_id] || "#443E38"}` }}>
              <span className="mt-0.5 text-primary shrink-0">{p.action === "new_sub" ? <GitBranchPlus size={16} /> : <Tag size={16} />}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.action === "new_sub" ? t("Nuovo sotto-ramo in {b}", { b: BRANCH_TITLES[p.branch_id] }) : t("Nuovo concetto in «{p}»", { p: p.parent_title })}</div>
                <div className="text-sm font-medium mt-0.5">{p.action === "new_sub" ? p.title : p.concept}</div>
                {p.summary && <p className="text-xs text-foreground/80 mt-1">{p.summary}</p>}
                {p.concepts?.length > 0 && <div className="flex flex-wrap gap-1 mt-1.5">{p.concepts.map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-full border border-border text-muted-foreground">{c}</span>)}</div>}
                <p className="text-xs text-muted-foreground mt-1 italic">{p.rationale}</p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <Button data-testid={`proposal-approve-${p.id}`} size="sm" className="h-7 px-2" onClick={() => decide(p.id, "approve")}><Check size={13} className="mr-1" /> {t("Approva")}</Button>
                <Button data-testid={`proposal-reject-${p.id}`} size="sm" variant="outline" className="h-7 px-2" onClick={() => decide(p.id, "reject")}><X size={13} className="mr-1" /> {t("Scarta")}</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
